import React, { useState, useEffect } from "react";
import Lightbox from "../../../static/Lightbox/src/Lightbox";
import { serializeVideoData, debounce, resizeVideos } from "../../../helpers";
import {
  getLatestVideos,
  playlistSearch,
  errorHandling
} from "../../../services";
import YouTubeEmbed from "../../YouTube";
import moment from "moment";
import "./index.scss";

function fetchData({
  playlistId = "",
  setVideoLoaded,
  videoFeedData
}: {
  playlistId?: string;
  videoLoaded?: boolean;
  setVideoLoaded: Function;
  videoFeedData: {
    items: Record<string, unknown>[];
  };
}) {
  const [data, updateData] = useState<any[]>([]);
  useEffect(() => {
    if (videoFeedData && videoFeedData.items.length) {
      updateData(serializeVideoData(videoFeedData.items));
      setVideoLoaded(true);
    } else {
      getLatestVideos({ id: playlistId })
        .then(res => {
          updateData(serializeVideoData(res.items));
          setVideoLoaded(true);
        })
        .catch(errorHandling);
    }
  }, [playlistId]);

  return data;
}

function fetchPlaylistData({ searchQuery }: { searchQuery: string }) {
  const [data, updateData] = useState<Record<string, unknown>[]>([]);
  useEffect(() => {
    if (searchQuery.length) {
      playlistSearch(searchQuery)
        .then(
          res =>
            new Promise(resolve => {
              if (res.items.length) {
                return getLatestVideos({ id: res.items[0].id.playlistId }).then(
                  data => resolve(data)
                );
              } else {
                updateData([]);
                return [];
              }
            })
        )
        .then((data: any) => updateData(serializeVideoData(data.items)))
        .catch(errorHandling);
    }
  }, [searchQuery]);
  return data;
}

interface ActiveVideo {
  thumbnails: any;
  id: string;
  title: string;
  publishedAt: Date;
  unset: boolean;
}

function VideoFeed({
  title = "Up next",
  videoFeedData = {
    items: []
  },
  playlistId,
  grid,
  callback,
  offset,
  PlayerState = { active: false },
  setPlayerState = () => {}
}: {
  title?: string;
  videoFeedData?: {
    items: Record<string, unknown>[];
  };
  playlistId: string;
  grid?: number;
  callback?: Function;
  offset?: number;
  PlayerState?: {
    active?: boolean;
  };
  setPlayerState?: Function;
}) {
  let [videoLoaded, setVideoLoaded] = useState(false);

  let data = fetchData({
    playlistId,
    videoLoaded,
    setVideoLoaded,
    videoFeedData
  });
  if (offset) data = data.slice(offset);

  let [thumbGrid] = useState(grid || 3);
  let [searchQuery, setSearchQuery] = useState("");
  let [lightboxShow, setlightBox] = useState(false);
  let [activeVideo, setActiveVideo] = React.useState<ActiveVideo | null>({
    thumbnails: { high: { url: "" } },
    id: "",
    title: "",
    publishedAt: new Date(),
    unset: true
  });
  let publishedDate =
    data.length && activeVideo
      ? moment(activeVideo.publishedAt).format("DD MMMM YYYY")
      : "";
  let playlistData = fetchPlaylistData({ searchQuery });

  let pager = data
    .map((item: any, key: number) =>
      item && Number.isInteger(++key / thumbGrid) ? { thumbGrid } : null
    )
    .filter((value: any) => value)
    .map((item: any, page: number) => ({ page, ...item }));

  pager.push({
    page: pager.length,
    thumbGrid: data.length - pager.length * thumbGrid
  });

  const handleOnClick = (key: number, title: string, lightbox: boolean) => {
    setlightBox(true);
    setSearchQuery(title);
    setActiveVideo(lightbox ? playlistData[key] : data[key]);
    if (PlayerState) setPlayerState({ active: !PlayerState });
    if (callback) callback();
  };

  const VideoItem = ({
    item,
    index,
    display,
    lightbox = false
  }: {
    item: any;
    index: number;
    display?: string;
    lightbox?: boolean;
  }) => {
    const date = moment(item.publishedAt).fromNow();
    const publishedDate =
      date.indexOf("a ") >= 0 ? date.replace("a ", "1 ") : date;
    return (
      <div
        className={`video-feed__list-item video-feed__list-item-${index} ${display}`}
      >
        <div className="video-feed__video video-resize">
          {item.thumbnails &&
          item.thumbnails.high &&
          item.thumbnails.high.url ? (
            <img src={item.thumbnails.high.url} alt={item.title} />
          ) : null}
          <button
            onClick={() => handleOnClick(index, item.title, lightbox)}
            className="btn--video-player"
          />
        </div>
        <p>{item.title}</p>
        <span>{publishedDate}</span>
      </div>
    );
  };

  if (data.length && activeVideo && activeVideo.unset) {
    setActiveVideo(data[0]);
  }

  useEffect(() => {
    debounce({
      func: () => {
        resizeVideos({ ElClassName: "video-resize" });
      }
    });
    resizeVideos({ ElClassName: "video-resize" });
  });

  return (
    <>
      <p className="video-feed__title--mobile">{title}</p>
      <div
        className={`video-feed${
          playlistData.length === 0 ? " video-feed--no-feed" : ""
        }${!videoLoaded ? " isLoading" : ""}`}
      >
        <p className="video-feed__title">{title}</p>
        <div className="video-feed__list">
          {data.length > 0 && videoLoaded ? (
            data.map((video: any, key: number) =>
              video.thumbnails ? (
                <VideoItem index={key} key={key} item={video} />
              ) : null
            )
          ) : (
            <span className="video-feed__description">
              No related videos found.
            </span>
          )}
        </div>
        {data.length ? (
          <Lightbox
            show={lightboxShow}
            callback={() => {
              setlightBox(false);
              setPlayerState({ active: PlayerState.active });
            }}
          >
            <div className="lightbox__video">
              <div className="lightbox__video-container">
                <YouTubeEmbed
                  mute={false}
                  id={`${activeVideo && activeVideo.id ? activeVideo.id : ""}`}
                  autoplay={1}
                  onEnd={() => {
                    setlightBox(false);
                  }}
                />
                {activeVideo &&
                activeVideo.thumbnails &&
                activeVideo.thumbnails.high &&
                activeVideo.thumbnails.high.url ? (
                  <img
                    src={activeVideo.thumbnails.high.url}
                    alt={activeVideo.title}
                  />
                ) : null}
              </div>
              <p className="lightbox__video-heading lightbox__video-heading--uppercase">
                {activeVideo && activeVideo.title ? activeVideo.title : ""}
              </p>
              <p className="lightbox__video-meta">{`Published on ${publishedDate}`}</p>
            </div>
            <div className="lightbox__video-feed">
              <p className="lightbox__video-heading">Up Next</p>
              <div className="lightbox__video-feed-list">
                {playlistData.map((item: any, key: number) => (
                  <VideoItem
                    index={key}
                    key={key}
                    item={item}
                    lightbox={true}
                  />
                ))}
              </div>
            </div>
          </Lightbox>
        ) : null}
      </div>
    </>
  );
}

export default VideoFeed;
